/**
 * Horizontal step tiles for the company "how to add a template" guide — same visual language as the trainee training pathway.
 */
import React from "react";
import { translateUi } from "../i18n/index";
import { Award, Calendar, ChevronRight, Edit2, Eye, FileText, Plus, Users } from "../icons";
import { cbCn } from "./cycleBuilderTheme";

type StepStyle = {
  kicker: string;
  icon: React.FC<{ className?: string }>;
  ring: string;
  bg: string;
  bgDark: string;
  text: string;
  connector: string;
};

const WORKFLOW_STEPS: Array<{
  id: string;
  title: string;
  description: string;
  style: StepStyle;
  badge?: string;
}> = [
  {
    id: "view",
    title: translateUi("View criteria"),
    description: translateUi("Preview units and criteria before you add the template."),
    style: {
      kicker: translateUi("Preview"),
      icon: Eye,
      ring: "ring-sky-500/30",
      bg: "bg-sky-50 border-sky-200",
      bgDark: "bg-sky-950/40 border-sky-800",
      text: "text-sky-700",
      connector: "from-sky-400 to-sky-600",
    },
  },
  {
    id: "add",
    title: translateUi("Add template"),
    description: translateUi("Adds the template as a draft in your cycles list."),
    style: {
      kicker: translateUi("Draft"),
      icon: Plus,
      ring: "ring-indigo-500/30",
      bg: "bg-indigo-50 border-indigo-200",
      bgDark: "bg-indigo-950/40 border-indigo-800",
      text: "text-indigo-700",
      connector: "from-indigo-400 to-indigo-600",
    },
  },
  {
    id: "events",
    title: translateUi("Add Events"),
    description: translateUi("On the cycle row, schedule assessment windows before release."),
    style: {
      kicker: translateUi("Schedule"),
      icon: Calendar,
      ring: "ring-amber-500/30",
      bg: "bg-amber-50 border-amber-200",
      bgDark: "bg-amber-950/40 border-amber-800",
      text: "text-amber-800",
      connector: "from-amber-400 to-amber-600",
    },
  },
  {
    id: "edit",
    title: translateUi("Edit"),
    description: translateUi("On editable templates only, customise criteria from the cycle row after adding."),
    badge: translateUi("Editable only"),
    style: {
      kicker: translateUi("Optional"),
      icon: Edit2,
      ring: "ring-violet-500/30",
      bg: "bg-violet-50 border-violet-200",
      bgDark: "bg-violet-950/40 border-violet-800",
      text: "text-violet-700",
      connector: "from-violet-400 to-violet-600",
    },
  },
  {
    id: "release",
    title: translateUi("Release"),
    description: translateUi("When events are saved, release the cycle so managers can assign it."),
    style: {
      kicker: translateUi("Go live"),
      icon: Award,
      ring: "ring-emerald-500/30",
      bg: "bg-emerald-50 border-emerald-200",
      bgDark: "bg-emerald-950/40 border-emerald-800",
      text: "text-emerald-700",
      connector: "from-emerald-400 to-emerald-600",
    },
  },
];

function FlowConnector({ isDarkMode, gradient }: { isDarkMode: boolean; gradient: string }) {
  return (
    <div className="flex w-6 shrink-0 items-center self-center sm:w-10" aria-hidden>
      <div className={cbCn("h-0.5 flex-1 rounded-full bg-gradient-to-r", gradient, isDarkMode ? "opacity-70" : "opacity-90")} />
      <ChevronRight className={cbCn("w-4 h-4 -ml-1 shrink-0", isDarkMode ? "text-slate-500" : "text-slate-400")} />
    </div>
  );
}

export function CompanyTemplateWorkflowFlow({ isDarkMode = false }: { isDarkMode?: boolean }) {
  const lastStyle = WORKFLOW_STEPS[WORKFLOW_STEPS.length - 1].style;

  return (
    <div
      className={cbCn(
        "overflow-hidden rounded-xl border",
        isDarkMode ? "border-slate-700 bg-slate-900/50" : "border-indigo-200 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/40"
      )}
    >
      <div
        className={cbCn(
          "flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5",
          isDarkMode ? "border-slate-700 bg-slate-800/40" : "border-indigo-100 bg-white/80"
        )}
      >
        <p className={cbCn("text-xs font-semibold uppercase tracking-wide", isDarkMode ? "text-slate-400" : "text-indigo-700")}>
          {translateUi("How to add a template")}
        </p>
        <span className={cbCn("text-xs font-semibold", isDarkMode ? "text-slate-400" : "text-slate-500")}>
          {WORKFLOW_STEPS.length} {translateUi("steps")}
        </span>
      </div>

      <div className="overflow-x-auto">
        <div className="flex min-w-max items-stretch gap-0 px-3 py-4 sm:px-4 sm:py-5">
          <div className="flex w-[4.25rem] shrink-0 flex-col items-center self-center sm:w-20">
            <div
              className={cbCn(
                "flex h-10 w-10 items-center justify-center rounded-full ring-2",
                isDarkMode ? "bg-slate-800 text-slate-300 ring-slate-600" : "bg-white text-slate-600 shadow-sm ring-slate-300"
              )}
            >
              <FileText className="h-5 w-5" />
            </div>
            <span
              className={cbCn(
                "mt-1.5 text-center text-[10px] font-bold uppercase tracking-wide",
                isDarkMode ? "text-slate-400" : "text-slate-500"
              )}
            >
              {translateUi("Template")}
            </span>
          </div>

          {WORKFLOW_STEPS.map((step, index) => {
            const style = step.style;
            const Icon = style.icon;
            const prevStyle = index > 0 ? WORKFLOW_STEPS[index - 1].style : null;
            return (
              <React.Fragment key={step.id}>
                <FlowConnector isDarkMode={isDarkMode} gradient={prevStyle?.connector || style.connector} />
                <div className="flex w-36 shrink-0 flex-col self-stretch sm:w-44">
                  <div
                    className={cbCn(
                      "relative flex h-full min-h-[10.5rem] flex-col rounded-xl border-2 p-3 ring-2",
                      style.ring,
                      isDarkMode ? style.bgDark : style.bg
                    )}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <span
                        className={cbCn(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                          isDarkMode ? "bg-slate-900/60 text-slate-200" : "bg-white/90 text-slate-700 shadow-sm"
                        )}
                      >
                        {index + 1}
                      </span>
                      <Icon className={cbCn("h-4 w-4 shrink-0 opacity-80", style.text)} />
                    </div>
                    <p className={cbCn("mt-2 line-clamp-2 text-sm font-bold leading-snug", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      {step.title}
                    </p>
                    <p className={cbCn("mt-0.5 text-[10px] font-semibold uppercase tracking-wide", style.text)}>{style.kicker}</p>
                    <p className={cbCn("mt-2 flex-1 text-xs leading-snug", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                      {step.description}
                    </p>
                    {step.badge ? (
                      <div className="mt-auto pt-2">
                        <span
                          className={cbCn(
                            "inline-flex rounded px-1.5 py-0.5 text-[9px] font-bold uppercase",
                            isDarkMode ? "bg-violet-950/70 text-violet-200" : "bg-violet-100 text-violet-800"
                          )}
                        >
                          {step.badge}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          <FlowConnector isDarkMode={isDarkMode} gradient={lastStyle.connector} />

          <div className="flex w-[4.25rem] shrink-0 flex-col items-center self-center sm:w-20">
            <div
              className={cbCn(
                "flex h-10 w-10 items-center justify-center rounded-full ring-2",
                isDarkMode ? "bg-emerald-950 text-emerald-400 ring-emerald-700" : "bg-emerald-50 text-emerald-600 shadow-sm ring-emerald-300"
              )}
            >
              <Users className="h-5 w-5" />
            </div>
            <span
              className={cbCn(
                "mt-1.5 text-center text-[10px] font-bold uppercase tracking-wide",
                isDarkMode ? "text-emerald-400" : "text-emerald-600"
              )}
            >
              {translateUi("Managers")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
